import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRestClient } from '../../../core/api/RestClientContext';
import { OnlineUsersStatsPoint } from '../../../core/models/stats';
import { Card } from '../../../core/components/Card';
import { RefreshBtn } from '../../../core/components/Button';
import Dropdown, { DropdownOption } from '../../../core/components/DropDown';
import { TimeSeriesChart, TimeSeriesPoint } from '../../stats/components/charts/TimeSeriesChart';
import './OnlineUsersChart.css';

const PERIOD_OPTIONS: DropdownOption[] = [
    { value: '1d',  label: '1d'  },
    { value: '7d',  label: '7d'  },
    { value: '30d', label: '30d' },
];

function getFromParam(period: string): string {
    const d = new Date();
    d.setDate(d.getDate() - (period === '1d' ? 1 : period === '7d' ? 7 : 30));
    return d.toISOString();
}

function buildChartData(points: OnlineUsersStatsPoint[]): TimeSeriesPoint[] {
    return [...points]
        .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
        .map((p) => ({
            timestamp: new Date(p.ts).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }),
            maxCount: p.max ?? p.count ?? 0,
            avgCount: p.avg ?? p.count ?? 0,
        }));
}

export const OnlineUsersChart: React.FC = () => {
    const client = useRestClient();
    const [period, setPeriod] = useState('7d');

    const { data: points = [], isLoading, isError, error, refetch } = useQuery<OnlineUsersStatsPoint[]>({
        queryKey: ['online-user-stats', period],
        queryFn: () => client.getOnlineUserStats({ from: getFromParam(period) }),
        staleTime: 60_000,
    });

    const chartData = useMemo(() => buildChartData(points), [points]);
    const errorMsg = error instanceof Error ? error.message : 'Не удалось загрузить статистику';

    return (
        <Card className="ouc-card">
            <div className="ouc-header">
                <div>
                    <h3 className="ouc-title">Онлайн пользователи</h3>
                    <p className="ouc-subtitle">Макс. и среднее число онлайн за день</p>
                </div>
                <div className="ouc-actions">
                    <Dropdown
                        options={PERIOD_OPTIONS}
                        selectedValue={period}
                        onSelect={(opt) => opt && setPeriod(opt.value)}
                        placeholder="Период"
                        disabled={isLoading}
                        className="ouc-period-dropdown"
                    />
                    <RefreshBtn onClick={() => void refetch()} loading={isLoading} />
                </div>
            </div>

            {isError && <div className="ouc-error">{errorMsg}</div>}

            {!isLoading && !isError && chartData.length === 0 && (
                <div className="ouc-empty">Нет данных за выбранный период</div>
            )}

            {chartData.length > 0 && (
                <TimeSeriesChart
                    data={chartData}
                    series={[
                        { key: 'maxCount',  name: 'Макс.',    color: '#869bdf' },
                        { key: 'avgCount',  name: 'Среднее',  color: '#ecc689' },
                    ]}
                    yAxisLabel="Пользователей"
                    height={200}
                />
            )}
        </Card>
    );
};
